/* ==========================================================================
   Regional Compliance Landing Page Configs - SEO Pages for HotelStack.io
   Part 1: EU/UK, United States, UAE/Middle East
   ========================================================================== */

import type { LucideIcon } from 'lucide-react';
import {
  Shield, FileText, CreditCard, Lock, Scale, Building2,
  Users, Globe, Receipt, Landmark, BadgeCheck, ScrollText,
} from 'lucide-react';

export interface RegionalComplianceConfig {
  region: string;
  slug: string;
  title: string;
  subtitle: string;
  flag: string;
  complianceFeatures: Array<{
    icon: LucideIcon;
    title: string;
    description: string;
  }>;
  taxDetails: Array<{
    name: string;
    rate: string;
    description: string;
  }>;
  guestRegistration: string;
  dataProtection: string;
  faqItems: Array<{ question: string; answer: string }>;
}

/* -- EU / UK -------------------------------------------------------------- */

const EU_UK: RegionalComplianceConfig = {
  region: 'EU / United Kingdom',
  slug: 'eu-uk',
  title: 'EU & UK Hotel Compliance Software',
  subtitle:
    'Full GDPR compliance, EU VAT automation, PSD2/SCA payment security, and ePrivacy cookie consent built into every HotelStack.io workflow.',
  flag: 'EU',
  complianceFeatures: [
    {
      icon: Shield,
      title: 'GDPR Data Protection',
      description:
        'Automated data retention policies, right-to-erasure workflows, guest data export (DSAR), and consent management across all guest touchpoints.',
    },
    {
      icon: Receipt,
      title: 'EU VAT Automation',
      description:
        'Multi-rate VAT calculation for accommodation (reduced rate) and ancillary services (standard rate) across 27 EU member states and the UK.',
    },
    {
      icon: CreditCard,
      title: 'PSD2 / SCA Compliance',
      description:
        'Strong Customer Authentication for online payments, 3D Secure 2.0 integration, and SCA exemption handling for low-risk transactions.',
    },
    {
      icon: Lock,
      title: 'ePrivacy & Cookie Consent',
      description:
        'PECR and ePrivacy-compliant cookie banners, granular consent tracking, and analytics opt-in/opt-out for guest-facing booking engine.',
    },
    {
      icon: FileText,
      title: 'Police Registration Forms',
      description:
        'Automated police fiche generation for France, Italy, Spain, and Portugal. Guest identity capture at check-in with country-specific formats.',
    },
    {
      icon: Globe,
      title: 'Multi-Language & Multi-Currency',
      description:
        'Interface in 12 EU languages. Native EUR, GBP, CHF, SEK, and NOK billing with real-time exchange rates and localized invoice formats.',
    },
  ],
  taxDetails: [
    { name: 'Standard VAT (UK)', rate: '20%', description: 'Applied to accommodation and most hotel services in the United Kingdom.' },
    { name: 'Reduced VAT (France)', rate: '10%', description: 'Accommodation services in France benefit from the reduced TVA rate.' },
    { name: 'Reduced VAT (Germany)', rate: '7%', description: 'Short-term accommodation in Germany taxed at reduced Mehrwertsteuer rate.' },
    { name: 'Reduced VAT (Spain)', rate: '10%', description: 'Hospitality services in Spain under the IVA reducido rate.' },
    { name: 'Reduced VAT (Italy)', rate: '10%', description: 'Accommodation services in Italy at the IVA ridotta rate.' },
    { name: 'Tourist Tax (varies)', rate: 'City-specific', description: 'Taxe de sejour, Kurtaxe, tassa di soggiorno — automated per-city calculation.' },
  ],
  guestRegistration:
    'HotelStack.io automates guest registration across EU/UK jurisdictions. Police fiche forms are generated for France, Italy, Spain, and Portugal. Passport and ID scanning captures required identity fields at check-in, with data stored in GDPR-compliant encrypted storage. Automated submissions to national police systems where electronic filing is supported.',
  dataProtection:
    'Full GDPR Article 17 right-to-erasure workflows, Article 15 data subject access requests (DSAR) with one-click export, Article 30 records of processing activities, and Data Protection Impact Assessments (DPIA) for new processing activities. Data Processing Agreements (DPA) available for all sub-processors. UK GDPR and EU GDPR dual compliance maintained post-Brexit.',
  faqItems: [
    { question: 'How does HotelStack.io handle GDPR compliance for hotels?', answer: 'Our platform includes automated data retention policies, right-to-erasure workflows, guest data export capabilities (DSAR), consent management, and records of processing activities. All guest data is encrypted at rest and in transit with EU-based data residency options.' },
    { question: 'Does HotelStack.io support PSD2 Strong Customer Authentication?', answer: 'Yes. All online payments go through 3D Secure 2.0 for SCA compliance. We handle SCA exemptions for low-risk transactions, merchant-initiated transactions, and recurring payments to minimize friction while maintaining compliance.' },
    { question: 'How are EU VAT rates managed across different countries?', answer: 'HotelStack.io maintains a database of current VAT rates for all 27 EU member states and the UK. Accommodation-specific reduced rates are automatically applied, and the system updates rates when tax authorities announce changes.' },
    { question: 'Can the system generate police registration forms for multiple EU countries?', answer: 'Yes. Our check-in module generates country-specific police registration forms for France (fiche de police), Italy (schedina alloggiati), Spain (parte de viajeros), and Portugal (boletim de alojamento) with all required identity fields.' },
  ],
};

/* -- United States -------------------------------------------------------- */

const UNITED_STATES: RegionalComplianceConfig = {
  region: 'United States',
  slug: 'united-states',
  title: 'US Hotel Compliance & Tax Software',
  subtitle:
    'State-by-state tax automation, ADA accessibility tracking, PCI DSS Level 1 payment security, and occupancy tax filing for every US jurisdiction.',
  flag: 'US',
  complianceFeatures: [
    {
      icon: Landmark,
      title: 'State & Local Tax Automation',
      description:
        'Automated calculation of state sales tax, county/city occupancy tax, tourism improvement district fees, and convention center surcharges across all 50 states.',
    },
    {
      icon: Scale,
      title: 'ADA Accessibility Compliance',
      description:
        'Track ADA-compliant room inventory, generate accessibility reports, manage service animal policies, and ensure website WCAG 2.1 AA compliance.',
    },
    {
      icon: CreditCard,
      title: 'PCI DSS Level 1',
      description:
        'Tokenized card storage, point-to-point encryption, and automated PCI compliance reporting. Integration with Stripe, Square, and Heartland processors.',
    },
    {
      icon: Receipt,
      title: 'Occupancy Tax Filing',
      description:
        'Generate filing-ready returns for city, county, and state occupancy tax authorities. Support for online filing portals in major jurisdictions.',
    },
    {
      icon: Users,
      title: 'Labor Law Compliance',
      description:
        'State-specific overtime rules, minimum wage tracking, tip credit calculations, and union labor scheduling for cities with hospitality labor regulations.',
    },
    {
      icon: Shield,
      title: 'CCPA / State Privacy Laws',
      description:
        'California Consumer Privacy Act compliance, Virginia CDPA, Colorado CPA, and other state privacy law support with opt-out and data deletion workflows.',
    },
  ],
  taxDetails: [
    { name: 'State Sales Tax', rate: '0% - 7.25%', description: 'Varies by state. Applied to room charges and taxable ancillary services.' },
    { name: 'City Occupancy Tax', rate: '2% - 15%', description: 'City-level transient occupancy tax applied to room revenue.' },
    { name: 'County Tax', rate: '0.5% - 5%', description: 'County-level lodging or accommodation tax where applicable.' },
    { name: 'Tourism District Fee', rate: '1% - 3%', description: 'Tourism improvement district assessments in major metro areas.' },
    { name: 'Convention Surcharge', rate: '$1 - $5/night', description: 'Per-night flat-rate surcharges for convention center funding.' },
    { name: 'Resort Fee Disclosure', rate: 'Varies', description: 'Mandatory resort fee disclosure per FTC guidance and state junk fee laws.' },
  ],
  guestRegistration:
    'US hotel guest registration is managed at the property level with state-specific requirements. HotelStack.io captures guest identity, vehicle information where required, and generates registration cards compliant with state hospitality regulations. Digital signature capture and electronic record retention meet state-mandated record-keeping periods of 1 to 6 years depending on jurisdiction.',
  dataProtection:
    'HotelStack.io complies with CCPA (California), CDPA (Virginia), CPA (Colorado), CTDPA (Connecticut), and UCPA (Utah) privacy regulations. Guest data opt-out workflows, do-not-sell mechanisms, data deletion requests, and privacy notice generation are built in. SOC 2 Type II certified infrastructure with annual penetration testing and vulnerability assessments.',
  faqItems: [
    { question: 'How does HotelStack.io handle different tax rates across US states?', answer: 'Our tax engine maintains a comprehensive database of state, county, city, and special district tax rates for all US jurisdictions. Rates are automatically applied based on property location, and the system updates when tax authorities announce rate changes.' },
    { question: 'Does HotelStack.io support ADA compliance tracking?', answer: 'Yes. Track ADA-compliant room counts, mobility-accessible features, hearing-impaired accommodations, and service animal policies. Generate ADA compliance reports and ensure your booking engine meets WCAG 2.1 AA accessibility standards.' },
    { question: 'How is PCI DSS compliance maintained?', answer: 'All payment card data is tokenized at the point of entry. We maintain PCI DSS Level 1 certification with annual QSA audits, quarterly ASV scans, and point-to-point encryption. No raw card data is ever stored in the HotelStack.io platform.' },
    { question: 'Can the system generate occupancy tax returns for filing?', answer: 'Yes. HotelStack.io generates filing-ready occupancy tax returns for all major US jurisdictions. Monthly, quarterly, and annual return periods are supported with automated calculation of taxes collected, exemptions, and adjustments.' },
  ],
};

/* -- UAE / Middle East ---------------------------------------------------- */

const UAE_MIDDLE_EAST: RegionalComplianceConfig = {
  region: 'UAE / Middle East',
  slug: 'uae-middle-east',
  title: 'UAE & Middle East Hotel Compliance',
  subtitle:
    'Tourism Dirham automation, 5% VAT with TRN invoicing, DTCM guest registration, and Arabic RTL interface for Gulf hospitality operations.',
  flag: 'AE',
  complianceFeatures: [
    {
      icon: Receipt,
      title: 'Tourism Dirham Fee',
      description:
        'Automated Tourism Dirham calculation based on hotel star rating (AED 7-20 per room per night). DTCM-ready reports with one-click generation.',
    },
    {
      icon: FileText,
      title: '5% VAT & TRN Invoicing',
      description:
        'FTA-compliant 5% VAT calculation on taxable supplies. Tax invoices include TRN (Tax Registration Number), supply dates, and line-item tax breakdown.',
    },
    {
      icon: Users,
      title: 'DTCM Guest Registration',
      description:
        'Automated guest data capture compatible with Dubai Tourism and Commerce Marketing (DTCM) e-services portal and statistical reporting requirements.',
    },
    {
      icon: Globe,
      title: 'Arabic RTL Interface',
      description:
        'Full right-to-left Arabic interface for staff PMS and guest booking engine. Bilingual Arabic/English operations with seamless language switching.',
    },
    {
      icon: Shield,
      title: 'PDPL Data Protection',
      description:
        'UAE Personal Data Protection Law (Federal Decree-Law No. 45/2021) compliance with data processing registers, consent management, and cross-border transfer controls.',
    },
    {
      icon: Building2,
      title: 'Municipality Compliance',
      description:
        'Support for Abu Dhabi DCT, Sharjah SCTDA, and other emirate-specific tourism authority reporting and licensing requirements.',
    },
  ],
  taxDetails: [
    { name: 'UAE VAT', rate: '5%', description: 'Federal VAT on taxable supplies including room revenue and ancillary services.' },
    { name: 'Tourism Dirham (5-star)', rate: 'AED 20/room/night', description: 'Premium rate for five-star hotels and resorts in Dubai.' },
    { name: 'Tourism Dirham (4-star)', rate: 'AED 15/room/night', description: 'Standard rate for four-star hotels in Dubai.' },
    { name: 'Tourism Dirham (3-star)', rate: 'AED 10/room/night', description: 'Rate for three-star hotels in Dubai.' },
    { name: 'Tourism Dirham (1-2 star)', rate: 'AED 7/room/night', description: 'Base rate for one and two-star hotels in Dubai.' },
    { name: 'Municipality Fee', rate: '7% (Dubai)', description: 'Dubai Municipality fee applied to room revenue.' },
  ],
  guestRegistration:
    'HotelStack.io captures Emirates ID, passport, and visa details at check-in for submission to DTCM e-services. Automated guest nationality tracking generates tourism statistics required by Dubai Tourism. GCC national identification and international passport OCR scanning accelerate the registration process while maintaining compliance with immigration authority requirements.',
  dataProtection:
    'Compliant with UAE Federal Decree-Law No. 45/2021 (Personal Data Protection Law). Data processing registers, legitimate interest assessments, consent records, and cross-border data transfer impact assessments are built in. Data residency options within UAE data centers ensure compliance with data localization requirements for government and semi-government hotel operators.',
  faqItems: [
    { question: 'How does HotelStack.io calculate the Dubai Tourism Dirham?', answer: 'The system automatically applies the correct Tourism Dirham rate based on your hotel star classification (AED 7 to AED 20 per room per night). Monthly DTCM reports are generated with one click, showing total collections, room nights, and breakdowns by nationality.' },
    { question: 'Does the system support UAE VAT with TRN invoicing?', answer: 'Yes. All invoices include your Tax Registration Number (TRN), itemized VAT at 5%, supply dates, and comply with FTA tax invoice requirements. VAT return data is available for quarterly filing.' },
    { question: 'Is the Arabic interface a full RTL implementation?', answer: 'Yes. The Arabic interface is a complete right-to-left implementation covering the staff PMS, guest booking engine, invoices, reports, and all guest communications. Staff can switch between Arabic and English at any time.' },
    { question: 'Does HotelStack.io support emirate-specific compliance beyond Dubai?', answer: 'Yes. We support Abu Dhabi Department of Culture and Tourism (DCT), Sharjah Commerce and Tourism Development Authority (SCTDA), and Ras Al Khaimah Tourism Development Authority reporting requirements.' },
  ],
};

/* =========================================================================
   Exported Maps (Part 1 — EU/UK, US, UAE)
   ========================================================================= */

export { EU_UK, UNITED_STATES, UAE_MIDDLE_EAST };
